import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBuildingLots } from '../hooks/useBuildingLots';
import { useMeetings } from '../hooks/useMeetings';
import { useProcessStreet } from '../hooks/useProcessStreet';
import { getDocxSubjectTextFromUrl, getDocxReplaceFieldsFromUrl } from '../api/submissionService';
import { searchPlanNumbers, fetchFrontInboxes, fetchFrontChannels, fetchInboxChannels, fetchEmailSignatures, fetchSmataBuildingProfile, fetchSmataContacts, getBuildingManagers } from '../api/suggestionService';
import { toast } from 'react-toastify';
import { useMicrosoftAuth } from "../context/MicrosoftAuthContext";
import { handleFormSubmit } from '../utils/formSubmitHandler';
import { createHandleInputChange } from '../utils/formHandlers';

import api from '../api/axios';
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "../css/preview.css";
import useVersionCheck from "../hooks/useVersionCheck";
import TemplatePreviewModal from '../components/TemplatePreviewModal';
import FormHeader from '../components/features/form/FormHeader';
import ProcessStreetSection from '../components/features/form/ProcessStreetSection';
import CustomDocumentName from '../components/features/form/CustomDocumentName';
import WindowLocksSection from '../components/features/form/WindowLocksSection';
import MeetingSelectionSection from '../components/features/form/MeetingSelectionSection';
import SendingSection from '../components/features/form/SendingSection';
import DynamicMergeFields from '../components/features/form/DynamicMergeFields';
import FileUploadSection from '../components/features/form/FileUploadSection';
import ManagerEmailSection from '../components/features/form/ManagerEmailSection';
import PlanNumberSection from '../components/features/form/PlanNumberSection';
import InvoiceApproversSection from '../components/features/form/InvoiceApproversSection';
import AdvancedOptionsSection from '../components/features/form/AdvancedOptionsSection';
import SendRemindersSection from '../components/features/form/SendRemindersSection';
import SendLaterSection from '../components/features/form/SendLaterSection';
import SendToSection from '../components/features/form/SendToSection';
import LotsUserTypeSection from '../components/features/form/LotsUserTypeSection';
import LotsSection from '../components/features/form/LotsSection';
import CustomRecipientsSection from '../components/features/form/CustomRecipientsSection';
import RecipientSourceToggle from '../components/features/form/RecipientSourceToggle';
import SpecialLeviesSection from '../components/features/form/SpecialLeviesSection';
import AssociatedDateFieldsSection from '../components/features/form/AssociatedDateFieldsSection';
import FullColourModal from '../components/features/form/FullColourModal';
import AFSSCopyToLOSection from '../components/features/form/AFSSCopyToLOSection';
import WindowLocksApprovalSection from '../components/features/form/WindowLocksApprovalSection';
import NotesSection from '../components/features/form/NotesSection';
import ActionButtonsSection from '../components/features/form/ActionButtonsSection';
import { Loader2, AlertCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { usePdfUpload } from '../hooks/usePdfUpload';
import { useEmailChannels } from '../hooks/useEmailChannels';
import { useSignatures } from '../hooks/useSignatures';
import { useTemplateConfigs } from '../hooks/useTemplateConfigs';
import '../css/Submissionprogress.css';
import {
    ONLINE_VOTING_TEMPLATE_IDS,
    GENERIC_LETTER_NOTICE_TEMPLATE_ID,
    sendToOptions,
    sendingPreferenceOptions,
    getInitialFormData,
    INITIAL_ERRORS
} from '../utils/constants';
import { filterMeetingsForTemplate, validateCustomRecipients } from '../utils/formUtils';


const Submissionprogress = ({ isOpen, toggle }) => {
    const navigate = useNavigate();
    useVersionCheck();
    const { isDark } = useTheme();

    // Set body and html background color based on theme to prevent white flash
    useEffect(() => {
        document.body.style.backgroundColor = isDark ? '#000000' : '#ffffff';
        document.documentElement.style.backgroundColor = isDark ? '#000000' : '#ffffff';
        return () => {
            document.body.style.backgroundColor = '';
            document.documentElement.style.backgroundColor = '';
        };
    }, [isDark]);

    const { jwtToken, accessToken, user, userTeamData } = useMicrosoftAuth();
    console.log("userEmail", user);
    const userEmail = user?.email || user?.username;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState(getInitialFormData(userEmail));
    const [errors, setErrors] = useState(INITIAL_ERRORS);

    const {
        isMerging, uploadProgress, mergedPdfUrl, setMergedPdfUrl,
        mergedPdfName, setMergedPdfName, numPages, pageNumber, pdfError,
        swiperInstance, setSwiperInstance, handleMergePdfs,
        onDocumentLoadSuccess, onDocumentLoadError, goToNextPage, goToPrevPage, resetPdfState
    } = usePdfUpload(formData, setFormData, errors, setErrors);

    const {
        allInboxes, setAllInboxes, UserInboxes,
        allChannels, setAllChannels, inboxChannels, setInboxChannels,
        UserChannels, isLoadingInboxes, setIsLoadingInboxes,
        isLoadingChannels, setIsLoadingChannels, isDefaultChannel, setIsDefaultChannel,
        channelInput, setChannelInput, channelSuggestions, setChannelSuggestions,
        showChannelSuggestions, setShowChannelSuggestions, selectedChannelSuggestionIndex,
        setSelectedChannelSuggestionIndex, teammateEmails,
        showEmailField, setShowEmailField
    } = useEmailChannels(userEmail);

    const {
        excludedEmails, emailSignatures, setEmailSignatures,
        userEmailSignatures, isLoadingSignatures,
        setIsLoadingSignatures, showSignatureSelect, setShowSignatureSelect
    } = useSignatures(userEmail);

    const {
        mailroomConfigs, isLoadingConfigs,
        mergeFieldsConfig, templateData, setTemplateData,
        loadingTemplateFields, setLoadingTemplateFields, loadingEmailSubject,
        setLoadingEmailSubject, replaceFields, setReplaceFields,
        templateValidationMessage, setTemplateValidationMessage,
        showTemplatePreview, previewTemplates,
        highlightField, handleTemplatePreview, handleCloseTemplatePreview
    } = useTemplateConfigs();

    const [planNumberSuggestions, setPlanNumberSuggestions] = useState([]);
    const [availableApprovers, setAvailableApprovers] = useState([]);
    const [availableInternalApprovers, setAvailableInternalApprovers] = useState([]);
    const [isLoadingApprovers, setIsLoadingApprovers] = useState(false);
    const [emailSuggestions, setEmailSuggestions] = useState([]);
    const [showPlanSuggestions, setShowPlanSuggestions] = useState(false);
    const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

    const [showReminderDate, setShowReminderDate] = useState(true);
    const [showReminderFields, setShowReminderFields] = useState(true);
    const [showSendingPreferenceModal, setShowSendingPreferenceModal] = useState(false);
    const [pendingSendingPreference, setPendingSendingPreference] = useState(null);
    const [showPriorityModal, setShowPriorityModal] = useState(false);
    const [pendingPriority, setPendingPriority] = useState(null);
    const [showSingleSidedModal, setShowSingleSidedModal] = useState(false);
    const [pendingSingleSided, setPendingSingleSided] = useState(null);
    const [advancedTrigger, setAdvancedTrigger] = useState(null);
    const [showFullColourModal, setShowFullColourModal] = useState(false);
    const [showWindowLocks, setShowWindowLocks] = useState(false);
    const [isLoadingAssociatedDates, setIsLoadingAssociatedDates] = useState(false);
    const [expandedCategories, setExpandedCategories] = useState({});

    const isOnlineVotingTemplate = ONLINE_VOTING_TEMPLATE_IDS.includes(Number(formData.smata_config_id));

    // Tracks the source that auto-populated the event date/time: null = editable, 'strata' = locked
    // from Strata Master, 'ps' = locked from Process Street (CSMC-2118)
    const [eventDateSource, setEventDateSource] = useState(null);

    const {
        buildingsData,
        selectedBuildings,
        expandedBuildings,
        showBuildingDropdown,
        buildingSearchTerms,
        planNumberLots,
        setSelectedBuildings,
        setExpandedBuildings,
        setShowBuildingDropdown,
        setBuildingSearchTerms,
        handleBuildingSelection,
        toggleBuildingExpansion,
        handleBuildingSearch,
        getFilteredLots,
        handleSelectAllBuilding,
        updateBuildingSelectionsFromLots,
        getLotsByPlanNumber
    } = useBuildingLots(formData, setFormData);

    const {
        meetings,
        setMeetings,
        isLoadingMeetings,
        setIsLoadingMeetings,
        showMeetings,
        setShowMeetings,
        meetingInput,
        setMeetingInput,
        meetingSuggestions,
        setMeetingSuggestions,
        showMeetingSuggestions,
        setShowMeetingSuggestions,
        selectedMeetingSuggestionIndex,
        setSelectedMeetingSuggestionIndex,
        includeFinalised,
        setIncludeFinalised,
        fetchMeetingsForPlan,
        handleIncludeFinalisedChange,
        handleMeetingInputChange,
        handleMeetingSelect
    } = useMeetings(formData, setFormData, setErrors, isOnlineVotingTemplate, { validateSendingOptionWithSendToOptions, setEventDateSource });

    const {
        workflowRuns,
        setWorkflowRuns,
        isLoadingWorkflowRuns,
        setIsLoadingWorkflowRuns,
        showWorkflowRunSuggestions,
        setShowWorkflowRunSuggestions,
        workflowRunInput,
        setWorkflowRunInput,
        selectedWorkflowRunSuggestionIndex,
        setSelectedWorkflowRunSuggestionIndex,
        processStreetFormFields,
        setProcessStreetFormFields,
        fetchWorkflowRunsForPlan,
        handleWorkflowRunSelect
    } = useProcessStreet(formData, setFormData, setErrors, {
        handleMergePdfs,
        mailroomConfigs,
        validateSendingOptionWithSendToOptions,
        setMergedPdfUrl,
        setMergedPdfName,
        handleInputChange,
        replaceFields,
        setEventDateSource
    });

    // Refs for suggestion containers
    const planSuggestionsRef = useRef(null);
    const emailSuggestionsRef = useRef(null);
    const channelSuggestionsRef = useRef(null);
    const meetingSuggestionsRef = useRef(null);
    const pdfViewerRef = useRef(null);
    const buildingDropdownRef = useRef(null);

    // Function to search plan numbers as user types
    const handlePlanNumberSearch = async (value, includeInactive = false) => {
        setErrors(prev => ({
            ...prev,
            planNumberSuggestion: true,
        }));
        handleInputChange('planNumber', value);

        // Reset invoice approver data whenever the user modifies the plan number
        setAvailableApprovers([]);
        setAvailableInternalApprovers([]);
        setFormData(prevData => ({
            ...prevData,
            invoice_approvers_enabled: false,
            num_approvers_required: '',
            min_approver_amount: '',
            selected_external_approvers: [],
            selected_internal_approvers: [],
            hidden_internal_approvers: [],
            databaseName: '',
        }));

        if (formData.preload_from_process_street) {
            setWorkflowRuns([]);
            setFormData(prevData => ({ ...prevData, process_street_workflow_run: '' }));
            setWorkflowRunInput('');
            setProcessStreetFormFields(null);
        }

        if (value.length >= 2) {
            try {
                const newresults = []
                const results = await searchPlanNumbers(value, includeInactive);
                // results.forEach(result => {
                //     newresults.push(
                //         result.plan_number,
                //     )
                // });

                // setPlanNumberSuggestions(newresults);
                // setShowPlanSuggestions(newresults.length > 0);


                setPlanNumberSuggestions(results);
                setShowPlanSuggestions(results.length > 0);

                setSelectedSuggestionIndex(-1);
            } catch (error) {
                console.error('Error fetching plan number suggestions:', error);
            }
        } else {
            setPlanNumberSuggestions([]);
            setShowPlanSuggestions(false);
        }
    };

    // Function to search emails as user types
    const handleEmailSearch = async (value) => {
        setAllInboxes([]);
        setAllChannels([]);
        setInboxChannels([]);
        setChannelInput('');
        setEmailSignatures([]);
        setShowSignatureSelect(false);
        handleInputChange('signature_id', '');

        setFormData(prevData => ({ ...prevData, inbox_id: '', channel_id: '', channel_send_as: '' }));
        setErrors(prev => ({
            ...prev,
            emailSuggestion: true,
        }));
        handleInputChange('email', value);

        if (value.length >= 2) {
            try {
                const results = teammateEmails.filter(email =>
                    email.toLowerCase().includes(value.toLowerCase())
                );
                setEmailSuggestions(results);
                setShowEmailSuggestions(results.length > 0);
                setSelectedSuggestionIndex(-1);
            } catch (error) {
                console.error('Error fetching email suggestions:', error);
            }
        } else {
            setEmailSuggestions([]);
            setShowEmailSuggestions(false);
        }
    };

    // Fetch Smata building profile + contacts + strata managers and pre-populate invoice approver fields
    const fetchBuildingProfileAndPreload = async (planNumber, databaseName) => {
        setIsLoadingApprovers(true);
        try {
            const [profile, contacts, buildingManagers] = await Promise.all([
                fetchSmataBuildingProfile(planNumber, databaseName),
                fetchSmataContacts(planNumber, databaseName),
                getBuildingManagers(databaseName)
            ]);

            // External approvers dropdown — populated from the Smata contacts list.
            // Only include contacts with a Smata user account (user_id != null); contacts without
            // one cannot be set as approvers via the Smata API and would be silently dropped on submit.
            const contactApprovers = contacts
                .filter(c => c.user_id != null)
                .map(c => ({ id: c.id, userId: c.user_id, name: c.full_name }));
            setAvailableApprovers(contactApprovers);

            // Internal approvers dropdown — populated from the config-pages building managers endpoint.
            // smata_id is the Smata user ID; use it as the canonical id so it matches the
            // building-profile approver ids and gets sent correctly to the Smata API on update.
            const internalApproverOptions = buildingManagers.map(m => {
                const smataId = m.smata_id;
                const name = m.name ?? m.full_name ?? `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim();
                return { id: smataId, userId: smataId, name };
            });
            setAvailableInternalApprovers(internalApproverOptions);

            if (!profile) return;
            const oneOff = profile.building_rules?.one_off_invoices;
            if (!oneOff) return;

            // Pre-select external approvers by matching building-profile user ids against contacts.
            const externalBPApprovers = (oneOff.approvers || []).filter(a => a.approver_type === 'external');
            const preSelectedExternal = externalBPApprovers.map(bpA =>
                contactApprovers.find(c => c.userId === bpA.id) ?? { id: bpA.id, userId: bpA.id, name: bpA.full_name }
            );

            // Pre-select internal approvers by matching building-profile user ids against building managers.
            // Building managers (in internalApproverOptions) → shown in UI as selectable chips.
            // Strata managers / org admins (not in internalApproverOptions) → hidden from UI but still
            // passed to the backend so they are preserved on submit.
            const internalBPApprovers = (oneOff.approvers || []).filter(a => a.approver_type === 'internal');
            const visibleInternal = [];
            const hiddenInternal = [];
            internalBPApprovers.forEach(bpA => {
                const match = internalApproverOptions.find(m => m.userId === bpA.id);
                if (match) {
                    visibleInternal.push(match);
                } else {
                    hiddenInternal.push({ id: bpA.id, userId: bpA.id, name: bpA.full_name });
                }
            });

            setFormData(prev => ({
                ...prev,
                num_approvers_required: oneOff.external_approvers_needed ?? prev.num_approvers_required,
                min_approver_amount: oneOff.amount_is_over_external ?? prev.min_approver_amount,
                selected_external_approvers: preSelectedExternal,
                selected_internal_approvers: visibleInternal,
                hidden_internal_approvers: hiddenInternal,
            }));
        } finally {
            setIsLoadingApprovers(false);
        }
    };

    // Function to handle selection of a plan number suggestion
    const fetchAndPopulateAssociatedDates = async (planNumber, databaseName, associatedDateFields) => {
        if (!associatedDateFields?.length || !planNumber || !databaseName) return;
        setIsLoadingAssociatedDates(true);
        try {
            const response = await api.get('/strata-cloud/associated-dates', {
                params: { planNumber, databaseName }
            });
            if (response.data.success) {
                const { last_agm_date, last_egm_date, last_committee_date } = response.data.data;
                setFormData(prev => ({
                    ...prev,
                    ...(associatedDateFields.includes('last_agm_date') && last_agm_date ? { last_agm_date: last_agm_date.slice(0, 16) } : {}),
                    ...(associatedDateFields.includes('last_egm_date') && last_egm_date ? { last_egm_date: last_egm_date.slice(0, 16) } : {}),
                    ...(associatedDateFields.includes('last_committee_date') && last_committee_date ? { last_committee_date: last_committee_date.slice(0, 16) } : {}),
                }));
            }
        } catch (err) {
            console.error('fetchAndPopulateAssociatedDates error:', err.message);
        } finally {
            setIsLoadingAssociatedDates(false);
        }
    };

    const handlePlanNumberSelect = (planNumber) => {
        setErrors(prev => ({
            ...prev,
            planNumberSuggestion: false,
        }));
        setFormData(prevData => ({ ...prevData, selectedLots: [], lots_sprated: [], lotRangeStart: '', lotRangeEnd: '' }));
        handleInputChange('planNumber', planNumber.plan_number);
        handleInputChange('databaseName', planNumber.database_name);
        handleInputChange('branch_id', planNumber.branch_id || '');
        handleInputChange('buildingAddress', planNumber.address);
        handleInputChange('sm_name', planNumber.sm_name || '');
        handleInputChange('sm_email', planNumber.sm_email || '');
        handleInputChange('sm_phone', planNumber.sm_phone || '');
        handleInputChange('emailOverrideGroupingCategories', planNumber.email_override_grouping_categories || null);
        setPlanNumberSuggestions([]);
        setShowPlanSuggestions(false);
        console.log(planNumber.plan_number, planNumber.database_name, planNumber.branch_id);
        getLotsByPlanNumber(planNumber.plan_number, planNumber.database_name);

        setIncludeFinalised(false);
        fetchMeetingsForPlan(planNumber.plan_number, planNumber.database_name);
        // Smata uses the full site_plan_id (planType + planNumber), e.g. "SPTest"
        fetchBuildingProfileAndPreload(formData.planType + planNumber.plan_number, planNumber.database_name);

        if (formData.preload_from_process_street) {
            fetchWorkflowRunsForPlan(planNumber.plan_number);
        }

        if (formData.associated_date_fields?.length) {
            fetchAndPopulateAssociatedDates(planNumber.plan_number, planNumber.database_name, formData.associated_date_fields);
        }
    };

    const handleNonStrataPlanNumberSelect = (planNumber) => {
        setErrors(prev => ({
            ...prev,
            planNumberSuggestion: false,
        }));
        handleInputChange('planNumber', planNumber.plan_number);
        handleInputChange('databaseName', planNumber.database_name);
        handleInputChange('branch_id', planNumber.branch_id || '');
        handleInputChange('buildingAddress', planNumber.address);
        setPlanNumberSuggestions([]);
        setShowPlanSuggestions(false);
    };


    // Function to handle selection of an email suggestion
    const handleEmailSelect = async (email) => {
        setEmailSuggestions([]);
        setShowEmailSuggestions(false);

        // Update form data and clear errors immediately
        setErrors(prev => ({
            ...prev,
            emailSuggestion: false,
        }));
        handleInputChange('email', email);

        // Check if email is in excluded list
        const isExcluded = excludedEmails.some(excludedEmail =>
            excludedEmail.email && excludedEmail.email.toLowerCase() === email.toLowerCase()
        );

        setIsLoadingInboxes(true);
        setIsLoadingChannels(true);
        setIsLoadingSignatures(true);

        // Fetch inboxes and channels
        fetchFrontInboxes(email).then(inboxes => {
            setAllInboxes(inboxes);
            setIsLoadingInboxes(false);

            fetchFrontChannels(email).then(channels => {
                setAllChannels(channels);
                setDefaultChannel(channels);
                setIsLoadingChannels(false);
            });
        });

        // Fetch email signatures ONLY if email is in excluded list and "sending from other" is selected
        if (formData.sendingOption === 'sending-from-other' && isExcluded) {
            try {
                setShowSignatureSelect(true);
                const signatures = await fetchEmailSignatures(email);
                setEmailSignatures(signatures || []);

                // Show signature selection if there are signatures available
                if (signatures && signatures.length > 1) {
                    setShowSignatureSelect(true);
                } else {
                    setShowSignatureSelect(false);
                }
                handleInputChange('signature_id', '');
            } catch (error) {
                console.error('Error fetching email signatures:', error);
                setEmailSignatures([]);
                setShowSignatureSelect(false);
                handleInputChange('signature_id', '');
            } finally {
                setIsLoadingSignatures(false);
            }
        } else {
            // No signatures needed for non-excluded emails or "sending from me"
            setEmailSignatures([]);
            setShowSignatureSelect(false);
            handleInputChange('signature_id', '');
            setIsLoadingSignatures(false);
        }

    };

    const handleChannelInputChange = (e) => {
        const value = e.target.value;
        setChannelInput(value);
        // Required field validation
        setErrors(prev => ({ ...prev, channel: value.trim() === '' }));
        if (value.length >= 1) {
            // Only include channels with a send_as email attribute
            const filtered = allChannels.filter(
                (channel) =>
                    channel.send_as && (
                        channel.name.toLowerCase().includes(value.toLowerCase()) ||
                        channel.send_as.toLowerCase().includes(value.toLowerCase())
                    )
            );
            setChannelSuggestions(filtered);
            setShowChannelSuggestions(filtered.length > 0);
            setSelectedChannelSuggestionIndex(-1);

            // If the value doesn't match any suggestion, set the error
            const exactMatch = filtered.some(
                (channel) => channel.name.toLowerCase() === value.toLowerCase()
            );
            setErrors(prev => ({
                ...prev,
                channelSuggestion: !exactMatch,
            }));
        } else {
            setChannelSuggestions([]);
            setShowChannelSuggestions(false);
            setSelectedChannelSuggestionIndex(-1);
        }
        // Do not update formData.inbox_id until a suggestion is selected
    };

    const handleInboxChannelInputChange = (e) => {
        const value = e.target.value;
        setChannelInput(value);
        // Required field validation
        setErrors(prev => ({ ...prev, channel: value.trim() === '' }));
        if (value.length >= 1) {
            // Only include channels with a send_as email attribute
            const filtered = inboxChannels.filter(
                (channel) =>
                    channel.send_as && (
                        channel.name.toLowerCase().includes(value.toLowerCase()) ||
                        channel.send_as.toLowerCase().includes(value.toLowerCase())
                    )
            );
            setChannelSuggestions(filtered);
            setShowChannelSuggestions(filtered.length > 0);
            setSelectedChannelSuggestionIndex(-1);

            // If the value doesn't match any suggestion, set the error
            const exactMatch = filtered.some(
                (channel) => channel.name.toLowerCase() === value.toLowerCase()
            );
            setErrors(prev => ({
                ...prev,
                channelSuggestion: !exactMatch,
            }));
        } else {
            setChannelSuggestions([]);
            setShowChannelSuggestions(false);
            setSelectedChannelSuggestionIndex(-1);
        }
        // Do not update formData.inbox_id until a suggestion is selected
    };



    // Handler for Inbox input key down (keyboard navigation)
    const handleChannelInputKeyDown = (e) => {
        if (!showChannelSuggestions) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedChannelSuggestionIndex((prev) =>
                prev < channelSuggestions.length - 1 ? prev + 1 : prev
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedChannelSuggestionIndex((prev) => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter' && selectedChannelSuggestionIndex >= 0) {
            e.preventDefault();
            handleChannelSuggestionClick(channelSuggestions[selectedChannelSuggestionIndex]);
        } else if (e.key === 'Escape') {
            setShowChannelSuggestions(false);
        }
    };

    // Handler for clicking an Inbox suggestion
    const handleChannelSuggestionClick = (channel) => {
        setChannelInput(channel.name);
        setFormData((prev) => ({
            ...prev,
            channel_id: channel.id,
            channel_send_as: channel.send_as
        }));
        setChannelSuggestions([]);
        setShowChannelSuggestions(false);
        setErrors(prev => ({
            ...prev,
            channel: false,
            channelSuggestion: false
        }));
    };

    // Set Default Channel
    function setDefaultChannel(channels) {
        if (!channels || channels.length === 0) return null;

        let defaultChannel = null;
        let defaultIndex = -1;

        const priorities = [
            'jamesons.com.au',
            'cngproperty.com.au',
            'stratalogic.com.au',
            'cnginsuranceservices.com.au'
        ];

        for (const domain of priorities) {
            defaultIndex = channels.findIndex(
                channel => channel.send_as && channel.send_as.toLowerCase().includes(domain)
            );
            if (defaultIndex !== -1) {
                defaultChannel = channels[defaultIndex];
                break;
            }
        }

        if (!defaultChannel) {
            defaultChannel = channels[0];
            defaultIndex = 0;
        }

        setChannelInput(defaultChannel.name);
        setSelectedChannelSuggestionIndex(defaultIndex);

        setFormData((prev) => ({
            ...prev,
            channel_id: defaultChannel.id,
            channel_send_as: defaultChannel.send_as
        }));

        setErrors(prev => ({
            ...prev,
            channel: false,
            channelSuggestion: false
        }));

        return defaultChannel;
    }


    // Add event listener to close suggestions when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            // Close plan number suggestions if clicked outside
            if (planSuggestionsRef.current && !planSuggestionsRef.current.contains(event.target)) {
                setShowPlanSuggestions(false);
            }

            // Close email suggestions if clicked outside
            if (emailSuggestionsRef.current && !emailSuggestionsRef.current.contains(event.target)) {
                setShowEmailSuggestions(false);
            }

            if (channelSuggestionsRef.current && !channelSuggestionsRef.current.contains(event.target)) {
                setShowChannelSuggestions(false);
            }

            // Close meeting suggestions if clicked outside
            if (meetingSuggestionsRef.current && !meetingSuggestionsRef.current.contains(event.target)) {
                setShowMeetingSuggestions(false);
            }

            // Close building dropdown if clicked outside
            if (buildingDropdownRef.current && !buildingDropdownRef.current.contains(event.target)) {
                setShowBuildingDropdown(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Disable body scroll when color modal is open
    useEffect(() => {
        if (showFullColourModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [showFullColourModal]);

    useEffect(() => {
        if (!isOnlineVotingTemplate) return;
        if (!replaceFields || !replaceFields.includes('meeting_location')) return;
        const current = formData.mergeFieldsData?.meeting_location;
        if (current && current.trim() !== '') return;
        setFormData(prev => ({
            ...prev,
            mergeFieldsData: {
                ...(prev.mergeFieldsData || {}),
                meeting_location: 'Conducted by pre-meeting Written Vote only'
            }
        }));
    }, [isOnlineVotingTemplate, replaceFields]);

    useEffect(() => {
        if (UserChannels && UserChannels.length > 0) {
            setDefaultChannel(UserChannels);
        }
    }, [UserChannels]);

    // Handle keyboard navigation in suggestions
    const handlePlanKeyDown = (e) => {
        if (!showPlanSuggestions) return;

        // Down arrow
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedSuggestionIndex(prev =>
                (prev < planNumberSuggestions.length - 1) ? prev + 1 : prev
            );
        }
        // Up arrow
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedSuggestionIndex(prev => (prev > 0) ? prev - 1 : 0);
        }
        // Enter
        else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
            e.preventDefault();
            const selected = planNumberSuggestions[selectedSuggestionIndex];
            if (formData.recipientSource === 'non-strata') {
                handleNonStrataPlanNumberSelect(selected);
            } else {
                handlePlanNumberSelect(selected);
            }
        }
        // Escape
        else if (e.key === 'Escape') {
            setShowPlanSuggestions(false);
        }
    };

    const handleEmailKeyDown = (e) => {
        if (!showEmailSuggestions) return;

        // Down arrow
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedSuggestionIndex(prev =>
                (prev < emailSuggestions.length - 1) ? prev + 1 : prev
            );
        }
        // Up arrow
        else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedSuggestionIndex(prev => (prev > 0) ? prev - 1 : 0);
        }
        // Enter
        else if (e.key === 'Enter' && selectedSuggestionIndex >= 0) {
            e.preventDefault();
            handleEmailSelect(emailSuggestions[selectedSuggestionIndex]);
        }
        // Escape
        else if (e.key === 'Escape') {
            setShowEmailSuggestions(false);
        }
    };

    const toggleColorPage = () => {
        if (formData.colorPages === 'true') {
            setFormData(prevData => ({ ...prevData, colorPages: 'false' }));
        } else {
            setShowFullColourModal(true);
        }
    };

    // Handle form input changes
    function handleInputChange(field, value) {
        return createHandleInputChange({
            user, formData, setFormData, setErrors, buildingsData,
            validateSendingOptionWithSendToOptions, validateCustomRecipients,
            updateBuildingSelectionsFromLots, setChannelInput, setIsDefaultChannel,
            setAllInboxes, setAllChannels, setEmailSignatures, setShowSignatureSelect,
            setShowEmailField, UserInboxes, UserChannels, excludedEmails,
            userEmailSignatures, setDefaultChannel, setMeetingInput,
            setMeetingSuggestions, setShowMeetingSuggestions, setSelectedMeetingSuggestionIndex,
            setShowReminderDate, setWorkflowRuns, setWorkflowRunInput,
            setPlanNumberSuggestions, setShowPlanSuggestions, setProcessStreetFormFields,
            setEventDateSource
        })(field, value);
    }

    // Handle form submission
    const handleSubmitWrapper = async () => {
        await handleFormSubmit({
            isSubmitting, setIsSubmitting, formData, setErrors, errors,
            channelInput, isDefaultChannel, showMeetings, isOnlineVotingTemplate,
            showWindowLocks, planNumberLots, mergeFieldsConfig, replaceFields,
            smataConfigId: formData.smata_config_id, showReminderFields,
            mergedPdfUrl, mergedPdfName, isMerging, userTeamData, jwtToken,
            accessToken, navigate
        });
    };


    // Reset form
    const resetForm = () => {
        const userEmail = user?.email || user?.username;
        setFormData(prev => ({
            ...getInitialFormData(userEmail),
            // Preserve specific fields that shouldn't be reset between submissions
            recipientSource: prev.recipientSource,
            branch_id: prev.branch_id,
            delivery_preference: prev.delivery_preference,
            sm_name: prev.sm_name,
            sm_email: prev.sm_email,
            sm_phone: prev.sm_phone,
            invoice_approvers_enabled: prev.invoice_approvers_enabled,
            num_approvers_required: prev.num_approvers_required,
            min_approver_amount: prev.min_approver_amount,
            min_internal_approver_amount: prev.min_internal_approver_amount,
            num_internal_approvers_required: prev.num_internal_approvers_required,
            internal_approvers_enabled: prev.internal_approvers_enabled,
            buildingAddress: prev.buildingAddress
        }));

        resetPdfState();

        // Reset meetings state
        setMeetings([]);
        setShowMeetings(false);
        setIsLoadingMeetings(false);
        setMeetingInput('');
        setMeetingSuggestions([]);
        setShowMeetingSuggestions(false);
        setSelectedMeetingSuggestionIndex(-1);
        setIncludeFinalised(false);

        // Reset workflow runs state
        setWorkflowRuns([]);
        setIsLoadingWorkflowRuns(false);
        setShowWorkflowRunSuggestions(false);
        setWorkflowRunInput('');
        setSelectedWorkflowRunSuggestionIndex(-1);

        // Reset buildings state
        setSelectedBuildings([]);
        setExpandedBuildings({});
        setShowBuildingDropdown(false);
        setBuildingSearchTerms({});

        setEmailSignatures([]);
        const isUserEmailExcluded = excludedEmails.some(excludedEmail =>
            excludedEmail.email && excludedEmail.email.toLowerCase() === userEmail.toLowerCase()
        );
        setShowSignatureSelect(isUserEmailExcluded && userEmailSignatures && userEmailSignatures.length > 1);
        setIsLoadingSignatures(false);

        setErrors({
            email: false,
            channel: false,
            channelSuggestion: false,
            meetingSuggestion: false,
            planNumber: false,
            sendTo: false,
            sending: false,
            windowLockApproval: false,
            contractorName: false,
            windowLockProceedWith: false,
            windowLockSpecialRequests: false,
            skip_meeting_update: false,
            fileUpload: false,
            mergedPDF: false,
            // Process Street fields
            process_street_workflow_run: false
        });
    };


    useEffect(() => {
        if (!formData.sending || !mailroomConfigs.length) return;
        const selectedConfig = mailroomConfigs.find(c => c.send_category === formData.sending);
        if (!selectedConfig) return;
        const isEmailOverride = Array.isArray(formData.emailOverrideGroupingCategories) &&
            formData.emailOverrideGroupingCategories.includes(Number(selectedConfig.grouping_category_id));
        setFormData(prev => ({ ...prev, sending_preference: isEmailOverride ? 'email' : '' }));
        setAdvancedTrigger({ expand: isEmailOverride, key: Date.now() });
    }, [formData.planNumber, formData.emailOverrideGroupingCategories]);


    function toggleCategoryExpansion(groupKey) {
        setExpandedCategories(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    async function handleSendingOptionSelect(config) {
        const {
            send_category, id: smata_config_id, smata_category, template_url, email_template_url,
            email_reminder_url, sms_reminder_url, agent_postal_template_url: agent_postal_template,
            agent_email_template_url: agent_email_template, agent_email_reminder_template_url: agent_email_reminder_template,
            agent_sms_reminder_template_url: agent_sms_reminder_template, resident_postal_template_url: resident_postal_template,
            resident_email_template_url: resident_email_template, resident_email_reminder_template_url: resident_email_reminder_template,
            resident_sms_reminder_template_url: resident_sms_reminder_template, issue_type, window_locks_strata_hub_automation
        } = config;

        var emailSubject = '';
        const documentName = send_category.replace(/\s*\(.*?\)/g, '').replace(/[^a-zA-Z0-9 ]/g, '');
        const REMINDER_CONFIG_IDS = [4, 5, 47, 53];
        const defaultReminders = REMINDER_CONFIG_IDS.includes(Number(smata_config_id)) ? 'yes' : 'no';
        setShowReminderDate(defaultReminders === 'yes');
        setShowWindowLocks(window_locks_strata_hub_automation === 1);
        setMeetingInput('');
        console.log('Selected sending option:', {
            send_category,
            smata_config_id,
            smata_category,
            issue_type
        });
        const send_to = formData.sendTo;
        const lotsUserTypes = formData.lotsUserTypes;
        const selectedConfigForOverride = mailroomConfigs.find(c => c.id == smata_config_id);
        const isEmailOverride = Array.isArray(formData.emailOverrideGroupingCategories) &&
            formData.emailOverrideGroupingCategories.includes(Number(selectedConfigForOverride?.grouping_category_id));


        const onlineVotingLocationDefault = 'Conducted by pre-meeting Written Vote only';
        const carriedMeetingLocation = formData.meetingLocation === onlineVotingLocationDefault ? '' : formData.meetingLocation;
        const carriedMergeFieldsData = formData.mergeFieldsData?.meeting_location === onlineVotingLocationDefault
            ? { ...formData.mergeFieldsData, meeting_location: '' }
            : formData.mergeFieldsData;

        setFormData({
            ...formData,
            sending: send_category,
            smata_config_id: smata_config_id,
            smata_category: smata_category,
            issueType: issue_type || '',
            selectedMeetingId: null,
            meetingName: '',
            meetingLocation: carriedMeetingLocation,
            mergeFieldsData: carriedMergeFieldsData,
            template_url: template_url,
            email_template_url: email_template_url,
            email_reminder_url: email_reminder_url,
            sms_reminder_url: sms_reminder_url,

            agent_postal_template: agent_postal_template,
            agent_email_template: agent_email_template,
            agent_email_reminder_template: agent_email_reminder_template,
            agent_sms_reminder_template: agent_sms_reminder_template,

            resident_postal_template: resident_postal_template,
            resident_email_template: resident_email_template,
            resident_email_reminder_template: resident_email_reminder_template,
            resident_sms_reminder_template: resident_sms_reminder_template,
            emailSubject: emailSubject,
            custom_document_name: Number(smata_config_id) === GENERIC_LETTER_NOTICE_TEMPLATE_ID
                ? ''
                : documentName.replace(/\s+/g, ' ').trim(),
            sendReminders: defaultReminders,
            reminderDate: '',
            windowLockApproval: '',
            contractorName: '',
            windowLockProceedWith: '',
            windowLockSpecialRequests: '',
            specialLeviesResolved: '',
            specialLeviesMotion: '',
            last_agm_date: '',
            last_egm_date: '',
            last_committee_date: '',
            associated_date_fields: send_category ? (mailroomConfigs.find(c => c.send_category === send_category)?.associated_date_fields || []) : [],
            skip_meeting_update: false,
            online_voting_open_for_voting: null,
            online_voting_show_on_portal: null,
            sending_preference: isEmailOverride ? 'email' : '',
        });
        setAdvancedTrigger({ expand: isEmailOverride, key: Date.now() });

        const newDateFields = send_category ? (mailroomConfigs.find(c => c.send_category === send_category)?.associated_date_fields || []) : [];
        if (newDateFields.length && formData.planNumber && formData.databaseName) {
            fetchAndPopulateAssociatedDates(formData.planNumber, formData.databaseName, newDateFields);
        }

        setErrors(prev => ({
            ...prev,
            custom_document_name: false,
            sending: false,
            sendingValidation: false,
        }));
        if (email_template_url) {
            setLoadingEmailSubject(true);
            try {
                const response = await getDocxSubjectTextFromUrl(email_template_url);
                emailSubject = (response.text || '').replace(/[‐-―−﹘﹣－]/g, '-');
                setFormData(prev => ({
                    ...prev,
                    emailSubject: emailSubject,
                }));
            } catch (e) {
                // subject stays blank, user can type manually
            } finally {
                setLoadingEmailSubject(false);
            }
        }
        validateSendingOptionWithSendToOptions(smata_config_id, send_to, lotsUserTypes);
    };
    async function validateSendingOptionWithSendToOptions(smata_config_id, send_to, lotsUserTypes = []) {
        setReplaceFields([]);
        var missedTemplate = false;
        console.log('validation', send_to);
        console.log('validation', smata_config_id);
        setTemplateValidationMessage('');
        setErrors(prev => ({
            ...prev,
            sendingValidation: false,
        }));
        setErrors(prev => ({
            ...prev,
            sendToValidation: false,
        }));
        if (send_to && smata_config_id) {
            let templateUrls = [];
            let templateTypes = [];
            let recipientTypes = [];
            const smata_config = mailroomConfigs.find(config => config.id === smata_config_id);
            const sendToArray = send_to.split('_');
            var showReminderFieldsForAny = false;
            setTemplateValidationMessage(smata_config.send_category + ' template not found');

            const isLotOnly = sendToArray.includes('lotOnly');

            const checks = [
                {
                    condition: (isLotOnly && lotsUserTypes.includes('owner')) || (!isLotOnly && (sendToArray.includes('owners') || sendToArray.includes('committeeMembers'))),
                    type: 'owner',
                    prefix: ''
                },
                {
                    condition: (isLotOnly && lotsUserTypes.includes('agent')) || (!isLotOnly && sendToArray.includes('agents')),
                    type: 'owner-agent',
                    prefix: 'agent_'
                },
                {
                    condition: (isLotOnly && lotsUserTypes.includes('residents-tenants')) || (!isLotOnly && (sendToArray.includes('residents') || sendToArray.includes('tenants'))),
                    type: 'tenant',
                    prefix: 'resident_'
                }
            ];

            checks.forEach(check => {
                if (!check.condition) return;

                const baseTemplateOriginalName = smata_config[`${check.prefix}email_template_original_name`];
                const baseTemplateUrl = smata_config[`${check.prefix}email_template_url`];
                const reminderOriginalName = smata_config[`${check.prefix}email_reminder_template_original_name`] || smata_config[`${check.prefix}email_reminder_original_name`];
                const reminderUrl = smata_config[`${check.prefix}email_reminder_template_url`] || smata_config[`${check.prefix}email_reminder_url`];
                const smsOriginalName = smata_config[`${check.prefix}sms_reminder_template_original_name`] || smata_config[`${check.prefix}sms_reminder_original_name`];
                const smsUrl = smata_config[`${check.prefix}sms_reminder_template_url`] || smata_config[`${check.prefix}sms_reminder_url`];

                if (!baseTemplateOriginalName) {
                    missedTemplate = true;
                    setErrors(prev => ({ ...prev, sendingValidation: true, sendToValidation: true }));
                } else {
                    templateUrls.push(baseTemplateUrl);
                    templateTypes.push('email');
                    recipientTypes.push(check.type);
                }

                if (reminderOriginalName) {
                    showReminderFieldsForAny = true;
                    templateUrls.push(reminderUrl);
                    templateTypes.push('email_reminder');
                    recipientTypes.push(check.type);
                }

                if (smsOriginalName) {
                    templateUrls.push(smsUrl);
                    templateTypes.push('sms');
                    recipientTypes.push(check.type);
                }
            });

            console.log('Final arrays:', {
                templateUrls,
                templateTypes,
                recipientTypes
            });
            setLoadingTemplateFields(true);
            try {

                const response = await getDocxReplaceFieldsFromUrl(
                    templateUrls.join(','),
                    templateTypes.join(','),
                    recipientTypes.join(',')
                );
                setReplaceFields(response.replaceFields);
                setTemplateData(response.templates || []);

                if (processStreetFormFields && response.replaceFields) {
                    const extraUpdates = {};
                    let newEvent = null;
                    let newLocation = null;

                    if ((response.replaceFields.includes('event_date') || response.replaceFields.includes('event_time')) &&
                        processStreetFormFields['Date_and_time_of_meeting_(MUST_include_time)']?.value &&
                        !formData.selectedMeetingId) {

                        const dateValue = processStreetFormFields['Date_and_time_of_meeting_(MUST_include_time)'].value;
                        const [datePart, timePart] = dateValue.split('T');
                        const [year, month, day] = (datePart || '').split('-');
                        const [hours, minutes] = (timePart || '00:00').split(':');
                        newEvent = `${year}-${month}-${day}T${hours}:${minutes}`;
                        setEventDateSource('ps');
                    }

                    if (response.replaceFields.includes('meeting_location') &&
                        processStreetFormFields.Location_of_meeting?.value) {
                        newLocation = processStreetFormFields.Location_of_meeting.value;
                        extraUpdates.meetingLocation = newLocation;
                    }

                    if (newEvent || newLocation) {
                        setFormData(prevData => ({
                            ...prevData,
                            ...extraUpdates,
                            mergeFieldsData: {
                                ...(prevData.mergeFieldsData || {}),
                                ...(newEvent ? { event: newEvent } : {}),
                                ...(newLocation ? { meeting_location: newLocation } : {})
                            }
                        }));
                    }
                }
            } catch (error) {
                console.error('Error fetching template fields:', error);
            } finally {
                setLoadingTemplateFields(false);
            }

            if (showReminderFieldsForAny) {
                setShowReminderFields(true);
            } else {
                setShowReminderFields(false);
            }

            console.log('validation', errors);
        }
    };



    const handleInstructionsClick = () => {
        window.open('https://app.frontapp.com/api/1/companies/d26b1babf6a198a0eaf8/knowledge_bases/4610/preview/articles/4641154?locale=en', '_blank');
    };

    return (
        <div className={`min-h-screen p-4 sm:p-6 lg:p-24 transition-colors duration-200 ${isDark ? 'bg-[#000000]' : 'bg-[#f5f5f5]'
            }`}>
            {/* Add style tag for suggestions */}


            {console.log(mergedPdfUrl, pageNumber)}

            {/* Form Container with Border */}
            <div className={`flex flex-col gap-[9px] items-center rounded-[12px] sm:rounded-[16px] lg:rounded-[20px] border-solid border-[1px] p-4 sm:p-6 lg:p-[24px] shadow-[0_0_40px_0_rgba(0,0,0,0.04)] transition-colors duration-200 ${isDark ? 'bg-[#161515] border-[#292929]' : 'bg-[#fff5eb] border-[#d9d9d9]'
                }`}>
                <FormHeader
                    onInstructionsClick={handleInstructionsClick}
                    onBingClick={() => window.open('Documents/Product Details.pdf', '_blank')}
                    isDark={isDark}
                />

                {/* Recipient Source Toggle - Strata Master vs Non-Strata */}
                <RecipientSourceToggle
                    formData={formData}
                    handleInputChange={handleInputChange}
                    isDark={isDark}
                />

                {/* Process Street Preload Section - Only show for Strata Master */}
                {formData.recipientSource === 'strata' && (
                    <ProcessStreetSection
                        formData={formData}
                        errors={errors}
                        isSubmitting={isSubmitting}
                        handleInputChange={handleInputChange}
                        planSuggestionsRef={planSuggestionsRef}
                        handlePlanNumberSearch={handlePlanNumberSearch}
                        handlePlanKeyDown={handlePlanKeyDown}
                        showPlanSuggestions={showPlanSuggestions}
                        planNumberSuggestions={planNumberSuggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        handlePlanNumberSelect={handlePlanNumberSelect}
                        workflowRunInput={workflowRunInput}
                        setWorkflowRunInput={setWorkflowRunInput}
                        showWorkflowRunSuggestions={showWorkflowRunSuggestions}
                        setShowWorkflowRunSuggestions={setShowWorkflowRunSuggestions}
                        workflowRuns={workflowRuns}
                        selectedWorkflowRunSuggestionIndex={selectedWorkflowRunSuggestionIndex}
                        setSelectedWorkflowRunSuggestionIndex={setSelectedWorkflowRunSuggestionIndex}
                        isLoadingWorkflowRuns={isLoadingWorkflowRuns}
                        handleWorkflowRunSelect={handleWorkflowRunSelect}
                        isDark={isDark}
                    />
                )}

                {/* Plan Number Field with Suggestions - Hidden when Process Street toggle is enabled */}
                {!formData.preload_from_process_street && (
                    <PlanNumberSection
                        formData={formData}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        planSuggestionsRef={planSuggestionsRef}
                        handlePlanNumberSearch={handlePlanNumberSearch}
                        handlePlanKeyDown={handlePlanKeyDown}
                        showPlanSuggestions={showPlanSuggestions}
                        planNumberSuggestions={planNumberSuggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        handlePlanNumberSelect={formData.recipientSource === 'non-strata' ? handleNonStrataPlanNumberSelect : handlePlanNumberSelect}
                        isDark={isDark}
                        optional={formData.recipientSource === 'non-strata'}
                    />
                )}
                {formData.recipientSource === 'strata' && errors.planNumber && (
                    <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                        <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
                        <span className="leading-[1.4]">You are required to fill this answer</span>
                    </div>
                )}
                {formData.recipientSource === 'strata' && errors.planNumberSuggestion && (
                    <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                        <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
                        <span className="leading-[1.4]">Please select a plan number from suggestions</span>
                    </div>
                )}

                {formData.recipientSource === 'strata' && (
                    <SendingSection
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        mailroomConfigs={mailroomConfigs}
                        isLoadingConfigs={isLoadingConfigs}
                        expandedCategories={expandedCategories}
                        toggleCategoryExpansion={toggleCategoryExpansion}
                        handleSendingOptionSelect={handleSendingOptionSelect}
                        handleInputChange={handleInputChange}
                        loadingEmailSubject={loadingEmailSubject}
                        templateValidationMessage={templateValidationMessage}
                        windowLocksSection={
                            <WindowLocksSection
                                showWindowLocks={showWindowLocks}
                                formData={formData}
                                errors={errors}
                                handleInputChange={handleInputChange}
                                isDark={isDark}
                            />
                        }
                        invoiceApproversSection={
                            <>
                                <SpecialLeviesSection
                                    formData={formData}
                                    errors={errors}
                                    handleInputChange={handleInputChange}
                                    isDark={isDark}
                                />
                                {formData.databaseName && [6, 7].includes(formData.smata_config_id) && (
                                    <InvoiceApproversSection
                                        formData={formData}
                                        handleInputChange={handleInputChange}
                                        availableApprovers={availableApprovers}
                                        availableInternalApprovers={availableInternalApprovers}
                                        isLoading={isLoadingApprovers}
                                        isDark={isDark}
                                        embedded
                                    />
                                )}
                            </>
                        }
                        isDark={isDark}
                    />
                )}

                {formData.recipientSource === 'strata' && (
                    <MeetingSelectionSection
                        show={showMeetings}
                        issueTypePresent={!!formData.issueType}
                        isLoadingMeetings={isLoadingMeetings}
                        meetings={filterMeetingsForTemplate(meetings, isOnlineVotingTemplate)}
                        meetingInput={meetingInput}
                        isSubmitting={isSubmitting}
                        handleMeetingInputChange={handleMeetingInputChange}
                        meetingSuggestions={meetingSuggestions}
                        setMeetingSuggestions={setMeetingSuggestions}
                        showMeetingSuggestions={showMeetingSuggestions}
                        setShowMeetingSuggestions={setShowMeetingSuggestions}
                        selectedMeetingSuggestionIndex={selectedMeetingSuggestionIndex}
                        setSelectedMeetingSuggestionIndex={setSelectedMeetingSuggestionIndex}
                        handleMeetingSelect={handleMeetingSelect}
                        meetingSuggestionsRef={meetingSuggestionsRef}
                        errors={errors}
                        setErrors={setErrors}
                        formData={formData}
                        setFormData={setFormData}
                        isOnlineVotingTemplate={isOnlineVotingTemplate}
                        includeFinalised={includeFinalised}
                        onIncludeFinalisedChange={handleIncludeFinalisedChange}
                        isDark={isDark}
                    />
                )}

                {formData.recipientSource === 'strata' && (
                    <AssociatedDateFieldsSection
                        formData={formData}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        isDark={isDark}
                        isLoading={isLoadingAssociatedDates}
                    />
                )}

                {(formData.recipientSource === 'strata' || formData.recipientSource === 'non-strata') && (
                    <CustomDocumentName
                        formData={formData}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        isDark={isDark}
                        isRequired={formData.recipientSource === 'strata' && Number(formData.smata_config_id) === GENERIC_LETTER_NOTICE_TEMPLATE_ID}
                    />
                )}

                <ManagerEmailSection
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    setErrors={setErrors}
                    handleInputChange={handleInputChange}
                    showEmailField={showEmailField}
                    showEmailSuggestions={showEmailSuggestions}
                    emailSuggestions={emailSuggestions}
                    selectedSuggestionIndex={selectedSuggestionIndex}
                    handleEmailSearch={handleEmailSearch}
                    handleEmailKeyDown={handleEmailKeyDown}
                    handleEmailSelect={handleEmailSelect}
                    emailSuggestionsRef={emailSuggestionsRef}
                    showSignatureSelect={showSignatureSelect}
                    isLoadingSignatures={isLoadingSignatures}
                    emailSignatures={emailSignatures}
                    userEmailSignatures={userEmailSignatures}
                    isDefaultChannel={isDefaultChannel}
                    setIsDefaultChannel={setIsDefaultChannel}
                    allInboxes={allInboxes}
                    isLoadingInboxes={isLoadingInboxes}
                    isLoadingChannels={isLoadingChannels}
                    channelInput={channelInput}
                    setChannelInput={setChannelInput}
                    channelSuggestions={channelSuggestions}
                    showChannelSuggestions={showChannelSuggestions}
                    setShowChannelSuggestions={setShowChannelSuggestions}
                    setChannelSuggestions={setChannelSuggestions}
                    selectedChannelSuggestionIndex={selectedChannelSuggestionIndex}
                    setSelectedChannelSuggestionIndex={setSelectedChannelSuggestionIndex}
                    handleChannelInputChange={handleChannelInputChange}
                    handleInboxChannelInputChange={handleInboxChannelInputChange}
                    handleChannelInputKeyDown={handleChannelInputKeyDown}
                    handleChannelSuggestionClick={handleChannelSuggestionClick}
                    channelSuggestionsRef={channelSuggestionsRef}
                    inboxChannels={inboxChannels}
                    allChannels={allChannels}
                    setInboxChannels={setInboxChannels}
                    setDefaultChannel={setDefaultChannel}
                    setIsLoadingChannels={setIsLoadingChannels}
                    fetchInboxChannels={fetchInboxChannels}
                    isSubmitting={isSubmitting}
                    isDark={isDark}
                />
                {(() => {
                    if (errors.email) {
                        return (
                            <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                                <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
                                <span className="leading-[1.4]">You are required to fill this answer</span>
                            </div>
                        );
                    }

                    if (errors.emailSuggestion) {
                        return (
                            <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                                <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
                                <span className="leading-[1.4]">You are required to select the email for Manager/Assist</span>
                            </div>
                        );
                    }

                    if (isDefaultChannel && errors.inbox) {
                        return (
                            <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                                <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
                                <span className="leading-[1.4]">You are required to select the shared inbox</span>
                            </div>
                        );
                    }

                    if (errors.channelSuggestion) {
                        return (
                            <div className="w-full max-w-[822px] rounded-[16px] px-[8px] flex items-start gap-[8px] text-[14px] font-semibold text-[#FF0000]">
                                <AlertCircle className="h-[16px] w-[16px] flex-shrink-0" />
                                <span className="leading-[1.4]">Please select an email from suggestions</span>
                            </div>
                        );
                    }

                    return null;
                })()}

                <FullColourModal
                    show={showFullColourModal}
                    onClose={() => setShowFullColourModal(false)}
                    onConfirm={() => {
                        setFormData(prevData => ({ ...prevData, colorPages: 'true' }));
                        setShowFullColourModal(false);
                    }}
                    isDark={isDark}
                />

                {/* Send To - Strata only */}
                {formData.recipientSource === 'strata' && (
                    <SendToSection
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                        sendToOptions={sendToOptions}
                        templateData={templateData}
                        handleTemplatePreview={handleTemplatePreview}
                        templateValidationMessage={templateValidationMessage}
                        isDark={isDark}
                    />
                )}

                {/* Lot # (Conditional on "Lot only" option) - Strata only */}
                {formData.recipientSource === 'strata' && formData.sendTo === 'lotOnly' && (
                    <>
                        <LotsUserTypeSection
                            formData={formData}
                            handleInputChange={handleInputChange}
                            errors={errors}
                            isDark={isDark}
                        />

                        <LotsSection
                            formData={formData}
                            handleInputChange={handleInputChange}
                            errors={errors}
                            setErrors={setErrors}
                            planNumberLots={planNumberLots}
                            buildingsData={buildingsData}
                            selectedBuildings={selectedBuildings}
                            setSelectedBuildings={setSelectedBuildings}
                            expandedBuildings={expandedBuildings}
                            setExpandedBuildings={setExpandedBuildings}
                            showBuildingDropdown={showBuildingDropdown}
                            setShowBuildingDropdown={setShowBuildingDropdown}
                            buildingSearchTerms={buildingSearchTerms}
                            setBuildingSearchTerms={setBuildingSearchTerms}
                            buildingDropdownRef={buildingDropdownRef}
                            handleBuildingSelection={handleBuildingSelection}
                            toggleBuildingExpansion={toggleBuildingExpansion}
                            handleBuildingSearch={handleBuildingSearch}
                            getFilteredLots={getFilteredLots}
                            handleSelectAllBuilding={handleSelectAllBuilding}
                            isDark={isDark}
                        />
                    </>
                )}

                {/* Recipient Details - Non-Strata only */}
                {formData.recipientSource === 'non-strata' && (
                    <CustomRecipientsSection
                        formData={formData}
                        handleInputChange={handleInputChange}
                        isDark={isDark}
                        errors={errors}
                    />
                )}

                {formData.recipientSource === 'strata' && loadingTemplateFields && (
                    <div className={`border-none rounded-[16px] p-[20px] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.11)] w-full max-w-[822px] flex flex-col items-center justify-center gap-[16px] transition-colors duration-200 ${isDark ? 'bg-[#000000]' : 'bg-white border border-[#d9d9d9]'
                        }`} style={{ minHeight: "100px" }}>
                        <Loader2 className={`w-[24px] h-[24px] animate-spin transition-colors duration-200 ${isDark ? 'text-[#b3b3b3]' : 'text-[#072837]'
                            }`} />
                        <div className={`text-[14px] font-normal transition-colors duration-200 ${isDark ? 'text-[#b3b3b3]' : 'text-[#898989]'
                            }`}>
                            Loading template fields...
                        </div>
                    </div>
                )}

                {formData.recipientSource === 'strata' && showReminderFields && !loadingTemplateFields && (
                    <>
                        {/* Reminders */}
                        <SendRemindersSection
                            formData={formData}
                            handleInputChange={handleInputChange}
                            showReminderDate={showReminderDate}
                            setShowReminderDate={setShowReminderDate}
                            isDark={isDark}
                        />
                    </>
                )}

                {formData.recipientSource === 'strata' && !loadingTemplateFields && (
                    <DynamicMergeFields
                        replaceFields={replaceFields}
                        mergeFieldsConfig={mergeFieldsConfig}
                        templateData={templateData}
                        formData={formData}
                        errors={errors}
                        handleInputChange={handleInputChange}
                        handleTemplatePreview={handleTemplatePreview}
                        eventDateSource={eventDateSource}
                        isDark={isDark}
                    />
                )}

                <AFSSCopyToLOSection
                    formData={formData}
                    handleInputChange={handleInputChange}
                    isDark={isDark}
                />

                <WindowLocksApprovalSection
                    formData={formData}
                    handleInputChange={handleInputChange}
                    isDark={isDark}
                />

                <NotesSection
                    formData={formData}
                    handleInputChange={handleInputChange}
                    isDark={isDark}
                />

                <FileUploadSection
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    mergedPdfUrl={mergedPdfUrl}
                    isMerging={isMerging}
                    uploadProgress={uploadProgress}
                    pdfError={pdfError}
                    numPages={numPages}
                    pageNumber={pageNumber}
                    swiperInstance={swiperInstance}
                    setSwiperInstance={setSwiperInstance}
                    onDocumentLoadSuccess={onDocumentLoadSuccess}
                    onDocumentLoadError={onDocumentLoadError}
                    goToNextPage={goToNextPage}
                    goToPrevPage={goToPrevPage}
                    toggleColorPage={toggleColorPage}
                    isDark={isDark}
                    onDuplicateFile={(fileName) => toast.error(`A file with the same name "${fileName}" is already attached.`)}
                />

                {/* Advanced Options */}
                <AdvancedOptionsSection
                    formData={formData}
                    handleInputChange={handleInputChange}
                    sendingPreferenceOptions={sendingPreferenceOptions}
                    showSendingPreferenceModal={showSendingPreferenceModal}
                    setShowSendingPreferenceModal={setShowSendingPreferenceModal}
                    pendingSendingPreference={pendingSendingPreference}
                    setPendingSendingPreference={setPendingSendingPreference}
                    showPriorityModal={showPriorityModal}
                    setShowPriorityModal={setShowPriorityModal}
                    pendingPriority={pendingPriority}
                    setPendingPriority={setPendingPriority}
                    showSingleSidedModal={showSingleSidedModal}
                    setShowSingleSidedModal={setShowSingleSidedModal}
                    pendingSingleSided={pendingSingleSided}
                    setPendingSingleSided={setPendingSingleSided}
                    isDark={isDark}
                    showStrataOptions={formData.recipientSource === 'strata'}
                    advancedTrigger={advancedTrigger}
                />

                {/* Send Later Section */}
                <SendLaterSection
                    formData={formData}
                    handleInputChange={handleInputChange}
                    errors={errors}
                    isDark={isDark}
                />

                <ActionButtonsSection
                    isSubmitting={isSubmitting}
                    handleSubmit={handleSubmitWrapper}
                    resetForm={resetForm}
                    isDark={isDark}
                />
            </div>

            {/* Template Preview Modal */}
            <TemplatePreviewModal
                isOpen={showTemplatePreview}
                onClose={handleCloseTemplatePreview}
                templates={previewTemplates}
                highlightField={highlightField}
                formData={formData}
                isDark={isDark}
            />
        </div>
    );
};

export default Submissionprogress;
